import React from 'react';
import {Button, ButtonGroup, Divider, Icon, Menu, MenuItem, Text} from '@mc/ui-v4';
import {mcRedo, mcUndo} from '@mc/react-ui-icons';
import './UndoButton.scss';
import {GeometryUndoStep} from './undoTypes';

type Props = {
    /** Zásobník kroků historie geometrie (od nejstaršího po nejnovější). */
    steps: GeometryUndoStep[];
    /** Počet kroků, které lze provést znovu ("Vpřed" / redo). */
    redoCount: number;
    /**
     * Vrátí historii chronologicky zpět až do stavu PŘED krokem na daném indexu (včetně).
     * Index odpovídá pozici v `steps` (0 = nejstarší, length-1 = nejnovější).
     */
    onUndoStep: (index: number) => void;
    /** Provede znovu poslední vrácený krok ("Vpřed" / redo). */
    onRedo: () => void;
    disabled?: boolean;
};

/**
 * Plovoucí ovládání "Zpět/Vpřed" pro změny geometrie (Editace/Posuv/Otočení/Offset).
 * Umísťuje se do horní lišty (Overhead) vedle ostatních ovládacích prvků, aby rozbalovací seznam
 * nekolidoval s vysouvacím detailem hlavního Toolbaru. Levé tlačítko vrátí poslední krok,
 * rozbalovací část nabídne seznam posledních kroků (max 5), pravé tlačítko provede krok znovu.
 * Historie je pouze klientská (do refreshe/zavření stránky).
 */
export const UndoButton: React.FC<Props> = ({steps, redoCount, onUndoStep, onRedo, disabled}) => {
    const hasUndo = steps.length > 0;
    const hasRedo = redoCount > 0;
    const undoDisabled = disabled || !hasUndo;
    const redoDisabled = disabled || !hasRedo;
    if (!hasUndo && !hasRedo) return null;
    return (
        <ButtonGroup className={'undo-redo-wrapper drop-shadow'}>
            <Button
                style={'link'}
                disabled={undoDisabled}
                title={'Zpět – vrátit poslední změnu geometrie (Ctrl+Z)'}
                onClick={() => hasUndo && onUndoStep(steps.length - 1)}
                icon={<Icon icon={mcUndo} />}
            />
            <Button
                style={'link'}
                disabled={undoDisabled}
                title={'Posledních 5 kroků'}
                dropdown
                popupProps={{position: 'bottom left'}}
                menu={
                    <Menu>
                        {steps
                            .map((step, index) => ({step, index}))
                            .reverse()
                            .map(({step, index}, pos) => (
                                <MenuItem
                                    key={step.id}
                                    onClick={() => onUndoStep(index)}
                                    title={`${step.label}${pos === 0 ? ' (poslední)' : ''}`}
                                    text={
                                        <Text className="pl-1">
                                            {steps.length - index}. {step.label}
                                            {pos === 0 ? ' (poslední)' : ''}
                                        </Text>
                                    }
                                />
                            ))}
                    </Menu>
                }
            />
            <Divider />
            <Button
                style={'link'}
                disabled={redoDisabled}
                title={'Vpřed – provést krok znovu (Ctrl+Y)'}
                onClick={() => hasRedo && onRedo()}
                icon={<Icon icon={mcRedo} />}
            />
        </ButtonGroup>
    );
};
